import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { FamilyHistoryRecord } from '@models/family-history';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useGetFamilyHistoryQuery,
  useListFamilyMemberHistoriesQuery,
  usePostFamilyMemberHistoryRecordMutation,
  useUpdateFamilyMemberHistoryRecordMutation,
  useDeleteFamilyMemberHistoryRecordMutation,
} from 'generated/family-history.generated';
import type {
  PostFamilyMemberHistoryRecordMutationVariables,
  UpdateFamilyMemberHistoryRecordMutationVariables,
  DeleteFamilyMemberHistoryRecordMutation,
  DeleteFamilyMemberHistoryRecordMutationVariables,
} from 'generated/family-history.generated';

export const useGetFamilyHistories = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListFamilyMemberHistoriesQuery({
    variables: {
      input: {
        patient: patient_id,
        count: 1500,
        page: 1,
      },
    },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listFamilyMemberHistories.data ?? []).map(
    (r) => new FamilyHistoryRecord(r)
  );
  return { ...response, data: records };
};

export const useGetFamilyHistory = (params: {
  record_id: string;
  skip?: boolean;
}) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetFamilyHistoryQuery({
    variables: {
      input: { record_id: params.record_id, patient_id },
    },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getFamilyMemberHistory;
  return { ...response, data: record ? new FamilyHistoryRecord(record) : null };
};

export function useCreateFamilyHistory(): [
  (
    params: PostFamilyMemberHistoryRecordMutationVariables
  ) => Promise<FetchResult<FamilyHistoryRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostFamilyMemberHistoryRecordMutation();
  const loading = baseLoading;

  async function createFamilyHistory(
    params: PostFamilyMemberHistoryRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postFamilyMemberHistoryRecord) {
          return;
        }
        const storeObject =
          response.postFamilyMemberHistoryRecord as unknown as StoreObject;
        const familyMemberHistory = params.input;
        try {
          cache.modify<{ listFamilyMemberHistories: CachedRecordList }>({
            fields: {
              listFamilyMemberHistories: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === familyMemberHistory.patient.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdFamHis = response.data?.postFamilyMemberHistoryRecord;
    return {
      ...response,
      data: createdFamHis ? new FamilyHistoryRecord(createdFamHis) : undefined,
    };
  }
  return [createFamilyHistory, loading];
}

export function useUpdateFamilyHistory(): [
  (
    params: UpdateFamilyMemberHistoryRecordMutationVariables
  ) => Promise<FetchResult<FamilyHistoryRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateFamilyMemberHistoryRecordMutation();
  const loading = baseLoading;

  async function updateFamilyHistory(
    params: UpdateFamilyMemberHistoryRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedFamHis = response.data?.updateFamilyMemberHistoryRecord;
    return {
      ...response,
      data: updatedFamHis ? new FamilyHistoryRecord(updatedFamHis) : undefined,
    };
  }
  return [updateFamilyHistory, loading];
}

export function useDeleteFamilyHistory(): [
  (
    params: DeleteFamilyMemberHistoryRecordMutationVariables
  ) => Promise<FetchResult<DeleteFamilyMemberHistoryRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteFamilyMemberHistoryRecordMutation();
  const loading = baseLoading;

  async function deleteFamilyHistory(
    params: DeleteFamilyMemberHistoryRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteFamilyMemberHistoryRecord) {
          return;
        }
        const { familymemberhistory_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'FamilyMemberHistoryModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteFamilyHistory, loading];
}
